export * from './tree-read'
import type { Blocks } from '@w/content-blocks/src/block'
import { removeBlock, spliceBlocks } from './ops'
import type { CoreState } from './state'
import { children, isRow, plain, type Row } from './tree-read'
import { blockClass } from './block-selection'

/** Normalizes the blocks a command wrote. A frozen block is the committed view no write of the command reached
 * (apply-intent.ts `draftBlocks`), so it is skipped: Lexical normalizes only the nodes an update marked dirty. */
export function clean(state: CoreState, items: Blocks, inline = false): void {
  for (let i = items.length - 1; i >= 0; i -= 1) {
    const block = items[i]
    if (Object.isFrozen(block)) continue
    clean(state, children(block), inline || isRow(block))
    // A populated nested-only item needs a leading paragraph for valid list markdown.
    if (block.type === 'li' && block.children[0]?.type === 'list' && plain(block)) {
      spliceBlocks(state, block.children, 0, 0, { id: '', type: 'p', children: [] })
    }
    // A list or container left with no blocks goes (Lexical removes an emptied parent that cannot be empty), as does
    // an emptied link.
    if ((block.type === 'a' || !inline && ['list', 'container'].includes(blockClass(block))) && children(block).length === 0) {
      spliceBlocks(state, items, i, 1)
    }
  }
  // Adjacent leaves never merge: a merge re-authors a leaf's text, which loses a peer's concurrent insert into it (crdt
  // facade spec "Join keeps identity"). Only an empty leaf beside an equal-format neighbour goes, carrying no text.
  // Adjacent links never merge either: deleting the right one loses a child a peer inserts into it. Range format keeps
  // a link it cuts and formats its leaves (apply-intent.ts), so no command needs a merge to rejoin its own copies.
  for (let i = 1; i < items.length; i += 1) {
    const left = items[i - 1]
    const right = items[i]
    if (left.type === 'chars' && right.type === 'chars' && (left.format ?? 0) === (right.format ?? 0) &&
      (!plain(left) || !plain(right))) {
      spliceBlocks(state, items, plain(right) ? i - 1 : i, 1)
      i -= 1
    }
  }
}

export function remove(state: CoreState, row: Row): void {
  removeBlock(state, row.block)
}
